import React, { useState, useRef } from "react";
import styled from "styled-components";
import { createPlaylist, updatePlaylist, Playlist } from "../../data/schema";
import { showToast } from "components/ui-kit/Toast";

type Props = {
  open: boolean;
  playlist?: Playlist | null;
  onClose: () => void;
  onSaved?: () => void;
};

const PLAYLIST_GRADIENTS = [
  'linear-gradient(135deg, #ff9a9e, #fecfef)',
  'linear-gradient(135deg, #a1c4fd, #c2e9fb)',
  'linear-gradient(135deg, #ffecd2, #fcb69f)',
  'linear-gradient(135deg, #ff9a56, #ffad56)',
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #f093fb, #f5576c)',
  'linear-gradient(135deg, #4facfe, #00f2fe)',
  'linear-gradient(135deg, #43e97b, #38f9d7)',
];

const getGradientForPlaylist = (playlistId?: string): string => {
  const id = playlistId || 'new';
  const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PLAYLIST_GRADIENTS[hash % PLAYLIST_GRADIENTS.length];
};

const getGradientFromKey = (key: string): string => {
  if (!key.startsWith('gradient:')) {
    return PLAYLIST_GRADIENTS[0];
  }
  const indexStr = key.split(':')[1] ?? '0';
  const index = parseInt(indexStr, 10);
  if (Number.isNaN(index)) return PLAYLIST_GRADIENTS[0];
  return PLAYLIST_GRADIENTS[((index % PLAYLIST_GRADIENTS.length) + PLAYLIST_GRADIENTS.length) % PLAYLIST_GRADIENTS.length];
};

const createRandomGradientKey = (): string => {
  const idx = Math.floor(Math.random() * PLAYLIST_GRADIENTS.length);
  return `gradient:${idx}`;
};

export const EditPlaylistModal: React.FC<Props> = ({ open, playlist, onClose, onSaved }) => {
  const [name, setName] = useState(playlist?.name || "");
  const [coverImage, setCoverImage] = useState<string | null>(playlist?.coverImage ?? null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setName(playlist?.name || "");
      if (playlist) {
        // Для существующих плейлистов используем сохранённое значение (URL или gradient:N)
        setCoverImage(playlist.coverImage ?? null);
      } else {
        // Новый плейлист — без градиента в модалке, просто пустой квадрат под загрузку
        setCoverImage(null);
      }
      setIsSubmitting(false);
    }
  }, [open, playlist]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setCoverImage(result);
    };
    reader.readAsDataURL(file);
  };

  const handleLoadPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    // Удаляем только загруженную картинку; цвет-градиент остаётся
    if (coverImage && coverImage.startsWith('gradient:')) {
      return;
    }
    setCoverImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (isSubmitting) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      showToast("Playlist name cannot be empty", undefined, "error");
      return;
    }

    setIsSubmitting(true);
    try {
    if (playlist) {
        await updatePlaylist(playlist.id, trimmedName, coverImage);
        showToast("Playlist updated", undefined, "success");
    } else {
        await createPlaylist(trimmedName, coverImage ?? undefined);
        showToast("Playlist created", undefined, "success");
    }
    onSaved?.();
    onClose();
    } catch (error) {
      console.error("Failed to save playlist", error);
      showToast("Unable to save playlist. Please try again.", undefined, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{playlist ? 'Update Playlist Details' : 'Create Playlist'}</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>
        
        <NameInput
          placeholder="Untitled"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <ImageSection>
          <ImagePreview onClick={handleLoadPhoto}>
            {coverImage ? (
              coverImage.startsWith('gradient:') ? (
                // При редактировании существующего плейлиста с градиентом показываем его
                <ImagePreviewPlaceholder $gradient={getGradientFromKey(coverImage)} />
              ) : (
                <ImagePreviewImg src={coverImage} alt="Playlist cover" />
              )
            ) : (
              <ImagePreviewEmpty>
                <ImagePreviewEmptyText>Tap to load photo</ImagePreviewEmptyText>
              </ImagePreviewEmpty>
            )}
          </ImagePreview>
          <ImageControls $hasImage={!!coverImage && !coverImage.startsWith('gradient:')}>
            <LoadPhotoButton onClick={handleLoadPhoto} $hasImage={!!coverImage && !coverImage.startsWith('gradient:')}>
              Load photo
            </LoadPhotoButton>
            <DeleteImageButton onClick={handleDeleteImage} $visible={!!coverImage && !coverImage.startsWith('gradient:')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </DeleteImageButton>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </ImageControls>
        </ImageSection>

        <Footer>
          <SaveButton onClick={handleSave} disabled={isSubmitting || !name.trim()}>
            {isSubmitting ? "Saving..." : "Save"}
          </SaveButton>
        </Footer>
      </Dialog>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 500px;
  max-width: calc(100% - 40px);
  background: #fff;
  color: #000;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
    padding: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #000;
`;

const CloseButton = styled.button`
  width: 3.4vh;
  height: 3.4vh;
  border-radius: 1.7vh;
  border: none;
  background: #f4f4f4;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
`;

const NameInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  font-size: 16px;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    border-color: #7987FF;
    background: #fff;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const ImagePreview = styled.div`
  width: 300px;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
`;

const ImagePreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImagePreviewPlaceholder = styled.div<{ $gradient: string }>`
  width: 100%;
  height: 100%;
  background: ${p => p.$gradient};
`;

const ImagePreviewEmpty = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  border: 2px dashed #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 14px;
  text-align: center;
`;

const ImagePreviewEmptyText = styled.span`
  padding: 0 16px;
`;

const ImageControls = styled.div<{ $hasImage?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${p => p.$hasImage ? '12px' : '0'};
  justify-content: flex-start;
  width: 300px;
  position: relative;
`;

const LoadPhotoButton = styled.button<{ $hasImage?: boolean }>`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  width: ${p => p.$hasImage ? 'calc(100% - 52px)' : '100%'};
  min-width: 0;
  
  &:hover {
    background: #f4f4f4;
    border-color: #ccc;
  }
`;

const DeleteImageButton = styled.button<{ $visible: boolean }>`
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #f4f4f4;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => p.$visible ? 1 : 0};
  pointer-events: ${p => p.$visible ? 'auto' : 'none'};
  transition: all 0.2s ease;
  flex-shrink: 0;
  width: ${p => p.$visible ? '40px' : '0'};
  overflow: hidden;
  padding: 0;
  min-width: 0;
  
  &:hover {
    background: #e9ecef;
    border-color: #ccc;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  background: #7987FF;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #6b7cff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #7987FF;
  }
`;

export default EditPlaylistModal;

